import { call, put, select, takeLatest } from "redux-saga/effects";
import * as studentLessonTypes from "Store/students/studentsLessons/constants";
import * as globalActions from "Store/global/actions";
import { setActionSpinner, setFetchSpinner } from "Store/global/actions";
import {
  addBulkAnswersToLessonApi,
  assignLessonToStudentApi,
  excuseStudentLessonLatenessApi,
  fetchAllCalendarLessonsApi,
  fetchStudentLessonApi,
  fetchStudentLessonListApi,
  fetchStudentLessonSectionApi,
  markAllLessonFlagsReviewedApi,
  rescheduleStudentLessonsApi,
  rescoreStudentLessonApi,
  resetStudentLessonsApi,
  startStudentLessonSectionApi,
  unAssignLessonFromStudentApi,
  updateStudentLessonProblemFlagApi,
  updateStudentLessonStatusApi
} from "Store/students/studentsLessons/api";
import { fetchLessonTemplateApi } from "Store/global/api";
import { Toast } from "utils/toast";
import { makeSelectActiveStudent, makeSelectStudentActiveLesson } from "Store/students/selectors";
import * as studentActions from "Store/students/studentsLessons/actions";
import { convertMetaToPagination, renderApiParameters } from "Store/global/saga";
import * as globalSelectors from "Store/global/selectors";

import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import { fetchStudentApi } from "Store/students/api";
import { setActiveStudent } from "Store/students/actions";

function* handleFetchStudentLessonsSaga(action) {
  yield put(setFetchSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "studentLessons");
    const activeStudent = yield select(makeSelectActiveStudent);
    const { data: lessons, meta } = yield fetchStudentLessonListApi(
      action.payload || activeStudent.id,
      parameters
    );
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "studentLessons"));
    yield put(studentActions.setStudentLessonList(lessons));
  } catch (e) {
    Toast.error(ERRORS.handleFetchStudentLessonsSaga, e.message);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleUpdateStudentLessonProblemFlagStatus(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield updateStudentLessonProblemFlagApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleFlagStudentLessonProblem, error);
    }
    yield put(studentActions.setStudentLessonProblemFlag(action.payload));
  } catch (error) {
    Toast.error(ERRORS.handleFlagStudentLessonProblem, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleStartStudentLessonSectionSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { lesson_section_id } = action.payload;
    const activeStudent = yield select(makeSelectActiveStudent);
    const activeLesson = yield select(makeSelectStudentActiveLesson);

    if (!lesson_section_id) {
      // Drill lessons have no sections — start the lesson itself
      const { error } = yield updateStudentLessonStatusApi({
        student_lesson_id: activeLesson.id,
        status: "STARTED"
      });
      if (error) {
        Toast.error(ERRORS.handleStartStudentLessonSection, error);
      } else {
        yield call(handleOpenLessonDetailsSaga, { item: { id: activeLesson.id } });
      }
      return;
    }

    const { error } = yield startStudentLessonSectionApi({
      lesson_section_id,
      student_lesson_id: activeLesson.id
    });
    if (error) {
      Toast.error(ERRORS.handleStartStudentLessonSection, error);
    } else {
      const { data } = yield fetchStudentLessonSectionApi(
        activeStudent.id,
        activeLesson.id,
        lesson_section_id
      );

      const sectionIndex = activeLesson.sections.findIndex(
        (section) => section.id === lesson_section_id
      );
      const section = activeLesson.sections[sectionIndex];

      activeLesson.problems = {
        ...activeLesson.problems,
        [section.name]: data.lesson_problems
      };
      activeLesson.sections[sectionIndex] = {
        ...activeLesson.sections[sectionIndex],
        status: "STARTED"
      };

      yield put(studentActions.setStudentActiveLesson(activeLesson));
    }
  } catch (error) {
    Toast.error(ERRORS.handleStartStudentLessonSection, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

export function* handleOpenLessonDetailsSaga(action) {
  try {
    const { id: studentId } = yield select(makeSelectActiveStudent);
    const { item } = action;
    const { data: fullLesson, error } = yield fetchStudentLessonApi(studentId, item.id);
    if (error) {
      Toast.error(ERRORS.handleOpenLessonDetailsSaga, error);
      return yield put(studentActions.setStudentActiveLesson(null));
    }
    let fetchedItem;
    if (fullLesson.status !== "ASSIGNED") {
      let problems = {};
      if ((fullLesson.type.label === "Module" || fullLesson.type.label === "Workbook Lesson") && fullLesson.sections) {
        const sections = fullLesson.sections;

        for (let i = 0; i < sections.length; i++) {
          problems[sections[i].name] = yield call(handleFetchLessonProblems, {
            student_id: studentId,
            lesson_id: item.id,
            section_id: sections[i].id,
            template_lesson_id: fullLesson.lesson_id
          });
        }
        fetchedItem = { ...fullLesson, ...problems };
        // Start fetching module lesson details
      } else if (fullLesson.type.label === "Drill") {
        problems = { drill: fullLesson.problems };
      }
      fetchedItem = { ...fullLesson, problems };
    } else {
      let problems = {};
      if (fullLesson.type.label === "Drill") {
        problems = { drill: fullLesson.problems };
      } else if (fullLesson.type.label === "Module" || fullLesson.type.label === "Workbook Lesson") {
        const { data: template } = yield fetchLessonTemplateApi(item.lesson_id);

        template.sections.forEach((section) => {
          problems = {
            ...problems,
            [section.name]: section.lesson_problems
          };
        });
      }
      fetchedItem = { ...fullLesson, problems };
    }
    yield put(studentActions.setStudentActiveLesson(fetchedItem));
    return fetchedItem;
  } catch (error) {
    Toast.error(ERRORS.handleOpenLessonDetailsSaga, error);
  }
}

function* handleSubmitLessonProblems(action) {
  yield put(setActionSpinner(true));
  try {
    const { section_id, answers } = action.payload;
    const activeStudent = yield select(makeSelectActiveStudent);
    const activeLesson = yield select(makeSelectStudentActiveLesson);

    const {
      type: { label: lessonType },
      id: lessonId
    } = activeLesson;

    if (lessonType !== "Reading" || lessonType !== "Assigned Reading") {
      const { error } = yield addBulkAnswersToLessonApi({
        section_id,
        answers,
        student_lesson_id: activeLesson.id
      });

      if (error) {
        return Toast.error(ERRORS.handleSubmitBulkLessonAnswers, error);
      } else {
        if (lessonType === "Module" || lessonType === "Workbook Lesson") {
          // Updating Sections:
          const sections = activeLesson.sections;

          const updatedSectionIndex = sections.findIndex((section) => section.id === section_id);
          sections[updatedSectionIndex] = { ...sections[updatedSectionIndex], status: "COMPLETED" };

          if (sections.every((section) => section.status === "COMPLETED")) {
            activeLesson.status = "COMPLETED";
          }
          activeLesson.sections = sections;
        }
        if (lessonType === "Drill" && activeLesson.status === "STARTED") {
          activeLesson.status = "COMPLETED";
        }
      }
      if (activeLesson.status === "COMPLETED") {
        const { error } = yield rescoreStudentLessonApi({
          student_lesson_id: lessonId,
          lesson_section_id: section_id
        });
        if (error) {
          return Toast.error(ERRORS.handleSubmitLessonProblems, error);
        }

        const { data: updatedLesson } = yield fetchStudentLessonApi(activeStudent.id, lessonId);

        activeLesson.scoring = updatedLesson.scoring;
      }
      yield put(studentActions.setStudentActiveLesson(activeLesson));

      Toast.success(SUCCESSES.handleSubmitLessonProblems);
    }
  } catch (error) {
    return Toast.error(ERRORS.handleSubmitLessonProblems, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

export function* handleFetchStudentCalendarLessonsSaga() {
  try {
    const activeStudent = yield select(makeSelectActiveStudent);
    if (activeStudent) {
      const { data, error } = yield call(fetchAllCalendarLessonsApi, activeStudent?.id);
      if (error) {
        return Toast.error(ERRORS.handleGetStudentCalendarLessons, error);
      }
      yield put(studentActions.setStudentLessonList(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleGetStudentCalendarLessons, error);
  }
}

function* handleAssignLessonsToStudentSaga(
  action: studentLessonTypes.IHandleAssignLessonsToStudent
) {
  yield put(globalActions.setActionSpinner(true));
  const { student_id } = action.payload;
  try {
    // @TODO: Probably needed to be paginate by lessons - to be checked for more lessons
    const { error } = yield assignLessonToStudentApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleAssignLessonsToStudentSaga, error);
      yield put(globalActions.setErrorSignal(true));
    } else {
      Toast.success(SUCCESSES.handleAssignLessonsToStudentSaga);
      if (action.fromList) {
        yield call(handleFetchStudentLessonsSaga, { payload: student_id });
      } else {
        yield call(handleFetchStudentCalendarLessonsSaga);
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleAssignLessonsToStudentSaga, error);
    yield put(globalActions.setErrorSignal(true));
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleRescheduleLessonsSaga(action) {
  const { student_lessons, student_id } = action.payload;
  yield put(globalActions.setActionSpinner(true));
  try {
    const { error } = yield call(rescheduleStudentLessonsApi, {
      student_lessons
    });
    if (error) {
      yield put(globalActions.setErrorSignal(true));
      return Toast.error(ERRORS.handleRescheduleStudentLessons, error);
    }
    Toast.success(SUCCESSES.handleRescheduleStudentLessons);

    yield put(globalActions.setFetchSpinner(true));
    if (action.fromList) {
      yield call(handleFetchStudentLessonsSaga, { payload: student_id });
    } else {
      yield call(handleFetchStudentCalendarLessonsSaga);
    }
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleStudentLessons, error);
  } finally {
    yield put(globalActions.setActionSpinner(false));
    yield put(globalActions.setFetchSpinner(false));
  }
}

function* handleExcuseStudentLessonLatenessSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield excuseStudentLessonLatenessApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleExcuseStudentLessonLatenessSaga, error);
    }
    const { id: studentId } = yield select(makeSelectActiveStudent);
    yield call(handleFetchStudentLessonsSaga, { payload: studentId });
    Toast.success(SUCCESSES.handleExcuseStudentLessonLatenessSaga);
  } catch (e) {
    return Toast.error(ERRORS.handleExcuseStudentLessonLatenessSaga, e.message);
  } finally {
    yield put(setActionSpinner(false));
  }
}

export function* handleFetchLessonProblems(action) {
  try {
    const { student_id, lesson_id, section_id, template_lesson_id } = action;
    const { data, error } = yield call(
      fetchStudentLessonSectionApi,
      student_id,
      lesson_id,
      section_id
    );
    if (error) {
      Toast.error(ERRORS.handleFetchLessonProblems, error);
      return false;
    }
    let problems;
    const { data: lessonTemplateData } = yield fetchLessonTemplateApi(template_lesson_id);

    if (data.lesson_problems.length === 0 || data.status === "ASSIGNED") {
      if (lessonTemplateData.type.label === "Module" || lessonTemplateData.type.label === "Workbook Lesson") {
        problems = lessonTemplateData.sections.find((s) => s.id === section_id).lesson_problems;
      } else {
        problems = lessonTemplateData.problems;
      }
    } else {
      problems = data.lesson_problems;
    }

    return problems;
  } catch (error) {
    Toast.error(ERRORS.handleFetchLessonProblems, error);
  }
}

function* handleMarkStudentLessonsFlagsReviewedSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield markAllLessonFlagsReviewedApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleMarkStudentLessonsFlagsReviewedSaga, error);
    } else {
      const { id: studentId } = yield select(makeSelectActiveStudent);
      if (action.payload.single) {
        yield call(handleOpenLessonDetailsSaga, {
          item: {
            id: action.payload.student_lesson_ids[0]
          }
        });
        const activeStudent = yield select(makeSelectActiveStudent);
        const { data: studentResponse } = yield fetchStudentApi(activeStudent.id);

        yield put(setActiveStudent({ ...studentResponse }));
      } else {
        yield call(handleFetchStudentLessonsSaga, { payload: studentId });
      }
      Toast.success(SUCCESSES.handleMarkStudentLessonsFlagsReviewedSaga);
    }
  } catch (error) {
    return Toast.error(ERRORS.handleMarkStudentLessonsFlagsReviewedSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleResetLessonsSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield resetStudentLessonsApi(action.payload);
    if (error) {
      Toast.error(ERRORS.handleResetLessons, error);
    } else {
      const { id: studentId } = yield select(makeSelectActiveStudent);
      yield call(handleFetchStudentLessonsSaga, { payload: studentId });
      Toast.success(SUCCESSES.handleResetLessons);
    }
  } catch (error) {
    Toast.error(ERRORS.handleResetLessons, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUnassignStudentLessonsSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { error } = yield unAssignLessonFromStudentApi(action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUnAssignLesson, error);
    }
    const { id: studentId } = yield select(makeSelectActiveStudent);
    yield call(handleFetchStudentLessonsSaga, { payload: studentId });
  } catch (error) {
    Toast.error(ERRORS.handleUnAssignLesson, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

export default function* studentsLessonsSaga() {
  yield takeLatest(
    studentLessonTypes.HANDLE_ASSIGN_LESSONS_TO_STUDENT,
    handleAssignLessonsToStudentSaga
  );
  yield takeLatest(studentLessonTypes.HANDLE_RESCHEDULE_LESSONS, handleRescheduleLessonsSaga);
  yield takeLatest(
    studentLessonTypes.HANDLE_START_STUDENT_LESSON_SECTION,
    handleStartStudentLessonSectionSaga
  );
  yield takeLatest(studentLessonTypes.HANDLE_FETCH_STUDENT_LESSONS, handleFetchStudentLessonsSaga);
  yield takeLatest(
    studentLessonTypes.HANDLE_EXCUSE_STUDENT_LESSON_LATENESS,
    handleExcuseStudentLessonLatenessSaga
  );

  yield takeLatest(
    studentLessonTypes.HANDLE_MARK_STUDENT_LESSONS_FLAGS_REVIEWED,
    handleMarkStudentLessonsFlagsReviewedSaga
  );
  yield takeLatest(studentLessonTypes.HANDLE_RESET_STUDENT_LESSONS, handleResetLessonsSaga);
  yield takeLatest(
    studentLessonTypes.HANDLE_UNASSIGN_STUDENT_LESSONS,
    handleUnassignStudentLessonsSaga
  );

  yield takeLatest(
    studentLessonTypes.HANDLE_UPDATE_STUDENT_LESSON_PROBLEM_FLAG_STATUS,
    handleUpdateStudentLessonProblemFlagStatus
  );

  yield takeLatest(
    studentLessonTypes.HANDLE_SUBMIT_STUDENT_LESSON_PROBLEMS,
    handleSubmitLessonProblems
  );
  yield takeLatest(
    studentLessonTypes.HANDLE_FETCH_STUDENT_CALENDAR_LESSONS,
    handleFetchStudentCalendarLessonsSaga
  );
}
